"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";
import VideoTestimonial, {
  VideoTestimonialHandle,
  VideoTestimonialProps,
} from "@/components/ui/video-testimonial";
import { throttle } from "lodash";
import Arrow from "@/components/ui/arrow";
import styles from "@/components/ui/video-testimonial-carousel.module.css";

export interface VideoTestimonialCarouselProps {
  slides: VideoTestimonialProps[];
}

const VideoTestimonialCarousel: React.FC<VideoTestimonialCarouselProps> = ({
  slides,
}) => {
  const VideoTestimonialCarouselRefs = useRef<VideoTestimonialHandle[]>([]);

  const addToVideoTestimonialCarouselRefs = (ref: VideoTestimonialHandle) => {
    if (ref) {
      VideoTestimonialCarouselRefs.current.push(ref);
    }
  };

  const [canScrollNext, setCanScrollNext] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    startIndex: 0,
    containScroll: false,
    slidesToScroll: 1,
    inViewThreshold: 0.8,
    duration: 10,
  });

  const stopAllVideos = useCallback(() => {
    VideoTestimonialCarouselRefs.current.forEach((ref) => {
      ref.pauseVideo();
    });
  }, []);

  const scrollPrev = useCallback(() => {
    stopAllVideos();
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi, stopAllVideos]);

  const scrollNext = useCallback(() => {
    stopAllVideos();
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi, stopAllVideos]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onScroll = useCallback(
    throttle(() => {
      if (!emblaApi) return;

      setCanScrollNext(emblaApi.canScrollNext());
      setCanScrollPrev(emblaApi.canScrollPrev());
    }, 100),
    [emblaApi]
  );

  useEffect(() => {
    if (!emblaApi) return;
    onScroll();

    emblaApi.on("scroll", onScroll);
  }, [emblaApi, onScroll]);

  return (
    <div className="flex w-full flex-col gap-2 md:gap-4">
      <div
        ref={emblaRef}
        className="flex w-full overflow-hidden"
      >
        <div className="flex w-full flex-row">
          {slides?.map((slide) => (
            <VideoTestimonial
              className={styles.embla__slide}
              key={slide.name}
              ref={addToVideoTestimonialCarouselRefs}
              {...slide}
            />
          ))}
        </div>
      </div>
      <div className="mx-auto flex flex-row gap-6 lg:ml-auto lg:mr-0">
        <Arrow
          onClick={scrollPrev}
          direction="left"
          disabled={!canScrollPrev}
          aria-label="Scroll to previous testimonials"
          aria-disabled={!canScrollPrev}
        />
        <Arrow
          onClick={scrollNext}
          direction="right"
          disabled={!canScrollNext}
          aria-label="Scroll to next testimonials"
          aria-disabled={!canScrollNext}
        />
      </div>
    </div>
  );
};

export default VideoTestimonialCarousel;
